import { echo } from "@/echo";
import { Error, MessageList, parseAxiosError } from "@/objects";
import { reportSupportRequest } from "@/sentry";
import { toStringArray } from "@/utils";
import { usePage } from "@inertiajs/vue3";
import axios from "axios";
import { AxiosError } from "axios";
import { defineStore } from "pinia";
import { computed, reactive, ref, toValue } from "vue";

export type GlobalSessionData = {
    userId: number,
    userLabel: string,
    sessionState: number,
    stopMsg?: string,
    start: number,
    end: number
}

export type StopCallback = (newState?: number, msg?: string) => void
export type UnpauseCallback = () => void
export type SupportReplyCallback = (msg: string) => void

export const useSessionStore = defineStore('session', () => {
    const state = reactive<GlobalSessionData>({
        userId: -1,
        userLabel: "",
        sessionState: -1,
        start: -1,
        end: -1
    })

    const supportMessages = ref<MessageList>([])

    // Replies the user hasn't seen yet: drives the dot on the "Supporto tecnico" button.
    const supportUnread = ref(false)

    const paused = computed(() => {
        return initialized.value && state.sessionState < 0
    })

    const onStopEvent = reactive<StopCallback[]>([])
    const onUnpauseEvent = reactive<UnpauseCallback[]>([])
    const onSupportReplyEvent = reactive<SupportReplyCallback[]>([])

    const initialized = ref(false)

    function initialize() {
        if (initialized.value) {
            console.warn("session store already initialized")
            return
        }

        const data = usePage().props["globalSessionData"]

        if (!data) {
            console.error("No global session data to initialize session store")
            return
        }

        state.userId = data["userId"]
        state.userLabel = data["userLabel"]
        state.sessionState = data["state"]
        state.stopMsg = data["stopMsg"]
        state.start = data["startTime"]
        state.end = data["endTime"]

        supportMessages.value = data["supportChat"]
        supportUnread.value = data["supportUnread"]

        listenTo("SessionStopped", (ev) => {
            state.sessionState = ev["new_state"]
            state.stopMsg = ev["msg"]

            console.log(`Session stopped (new state: ${ev['new_state']})`)

            onStopEvent.forEach((callback) => {
                callback(state.sessionState, state.stopMsg)
            })
        })

        listenTo("SessionUnpaused", (ev) => {
            state.sessionState = 1

            console.log(`Session unpaused`)

            onUnpauseEvent.forEach((callback) => {
                callback()
            })
        })

        listenTo("SupportReply", (ev) => {
            appendMsg(ev["message"], true)
            supportUnread.value = true

            onSupportReplyEvent.forEach((callback) => {
                callback(ev["message"])
            })
        })

        initialized.value = true
    }

    function appendMsg(msg: string, isReply = false) {
        const count = supportMessages.value.length

        // The parity check assumes an existing block to append to: on an empty chat every
        // message opens one, whoever wrote it.
        if (count === 0 || (count % 2 === 0) !== isReply) {
            supportMessages.value.push([msg])
        }
        else {
            const last = [...toStringArray(toValue(supportMessages)[count-1]), msg]

            supportMessages.value[count-1] = last
        }
    }

    async function sendSupportMsg(msg: string) {
        // Only the message that opens the thread goes to Sentry. The thread comes from the
        // server on every page load, so this stays false across reloads.
        const opensThread = supportMessages.value.length === 0

        try {
            const { data } = await axios.post('/support-msg', {
                target_user: state.userId,
                body: msg
            })

            appendMsg(msg)

            if (opensThread) {
                // Not awaited: the replay flush is a round trip the user shouldn't wait on.
                reportSupportRequest(msg, {
                    userId: state.userId,
                    userLabel: state.userLabel,
                })
            }

            return { ok: true, data }
        } catch (error) {
            handleError(error, null,'sendSupportMsg')

            return { ok: false, message: 'Ops! Qualcosa è andato storto. Riprova più tardi.' }
        }
    }
    
    /**
     * Clears the news dot, both here and server side, once the user has actually opened the
     * support chat. Failing to reach the server only means the dot comes back on reload.
     */
    async function markSupportRead() {
        if (!supportUnread.value) {
            return
        }

        supportUnread.value = false

        try {
            await axios.post('/support-msg/read')
        } catch (error) {
            handleError(error, null, 'markSupportRead')
        }
    }

    function listenTo(event: string, callback: (e: Object) => void) {
        return echo.private(`user.${state.userId}`).listen(event, callback)
    }

    return {
        state,
        paused,
        initialize,
        listenTo,
        onStopEvent,
        onUnpauseEvent,
        supportMessages,
        supportUnread,
        sendSupportMsg,
        markSupportRead
    }
})

export function handleError(err: AxiosError, callback: (err: Error) => void, caller: string) {
    console.error(`${caller} failed`, err)

    if (!callback) {
        return
    }

    callback(parseAxiosError(`${caller} failed`, err))
}